import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useMedia } from '@/features/media';

export interface PreviewSlide {
  mediaId: string;
  /** The network's 2:3 rendition once rendered. */
  url: string | null;
}

export interface PostPreviewProps {
  slides: PreviewSlide[];
  /** Library rows already loaded (the chosen products' galleries). */
  lookup: ReadonlyMap<string, Media>;
  title: string;
  boardName: string | null;
  altText: string;
  network: string;
}

function SlideImage({
  slide,
  known,
  alt,
}: {
  slide: PreviewSlide;
  known: Media | undefined;
  alt: string;
}) {
  const { data } = useMedia(slide.url || known ? null : slide.mediaId);
  const media = known ?? data;
  const src = slide.url ?? media?.renditions?.large ?? media?.url;
  if (!src) return <div className="bg-muted size-full animate-pulse" aria-hidden />;
  return <img src={src} alt={alt} className="size-full object-cover" />;
}

/**
 * The pin as Pinterest will crop it: a 2:3 frame per slide, cover-fit like the backend's
 * rendition, with the title and board under it. Carousels page through their slides.
 */
export function PostPreview({
  slides,
  lookup,
  title,
  boardName,
  altText,
  network,
}: PostPreviewProps) {
  const { t } = useTranslation('social');
  const [picked, setIndex] = useState(0);
  const count = slides.length;
  // A removed slide must not leave the pointer past the end.
  const index = Math.min(picked, Math.max(0, count - 1));
  const slide = slides[index];

  return (
    <figure className="flex flex-col gap-3" aria-label={t('editor.preview.title')}>
      <div className="bg-muted relative aspect-[2/3] w-full overflow-hidden rounded-xl border">
        {slide ? (
          <SlideImage slide={slide} known={lookup.get(slide.mediaId)} alt={altText} />
        ) : (
          <div className="text-muted-foreground flex size-full items-center justify-center p-6 text-center text-sm">
            {t('editor.preview.empty')}
          </div>
        )}
        {count > 1 ? (
          <span className="bg-background/80 absolute top-2 right-2 rounded-full px-2 py-0.5 text-xs tabular-nums">
            {t('editor.preview.slide', { n: index + 1, count })}
          </span>
        ) : null}
      </div>
      {count > 1 ? (
        <div
          className="flex items-center justify-center gap-1"
          role="group"
          aria-label={t('editor.preview.slides')}
        >
          {slides.map((s, i) => (
            <Button
              key={s.mediaId}
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={t('editor.preview.goTo', { n: i + 1 })}
              aria-pressed={i === index}
              onClick={() => setIndex(i)}
            >
              <span
                aria-hidden
                className={cn(
                  'block size-2 rounded-full',
                  i === index ? 'bg-foreground' : 'bg-muted-foreground/40',
                )}
              />
            </Button>
          ))}
        </div>
      ) : null}
      <figcaption className="flex flex-col gap-0.5">
        <span className={cn('truncate text-sm font-medium', !title && 'text-muted-foreground')}>
          {title || t('editor.preview.untitled')}
        </span>
        <span className="text-muted-foreground truncate text-xs">
          {boardName
            ? t('editor.preview.board', { board: boardName, network })
            : t('editor.preview.noBoard', { network })}
        </span>
      </figcaption>
    </figure>
  );
}
